import styles from './login-sms.scss';
import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import PinInput from 'react-pin-input';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import classnames from 'classnames/bind';

import LoginBasicBox from '@/components/auth/login-basic-box/login-basic-box';
import {
  REDIRECT_SEARCH_PARAM_KEY,
  TEMPORARY,
  MFA_DEST_MASKED,
} from '@/constants/system';
import { useLevvelTranslation } from '@/hooks/useLevvelTranslation';
import { useRuntimeConfig } from '@/hooks/useRuntimeConfig';

import { advanceLoginStep } from '@/pages/Auth/login-step';
import {
  MFA_RESEND_COOLDOWN_SECONDS,
  armMfaResendCountdown,
  readMfaResendRemaining,
} from '@/pages/Auth/mfa-countdown';
import { addToast, ToastType } from '@/store/system';

import { LoginResponse } from '@/services/api.types';
import {
  useLoginMfaVerifyMutation,
  useLoginMfaResendMutation,
} from '@/services/auth.api';

const cx = classnames.bind(styles);
const VERIFY_CODE_REGEX = /^[0-9]{6}$/;

const LoginSms = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state } = useLocation();
  const account = (state as any)?.account;
  const { lt } = useLevvelTranslation();
  const config = useRuntimeConfig();

  // tmp step token (aud=mfa) + masked destination handed over by the login step.
  // Kept in state (seeded from localStorage) so a resend can refresh the tmp token
  // and a page reload survives (batch 001; dest is no longer a token claim).
  const [tmpToken, setTmpToken] = useState(
    () => localStorage.getItem(TEMPORARY) ?? ''
  );
  const [destMasked, setDestMasked] = useState(
    () => localStorage.getItem(MFA_DEST_MASKED) ?? ''
  );

  // Resend cooldown (PD-2932). ONE piece of state: the seconds still left,
  // recovered from the instant the last code was sent (mfa-countdown.ts). It is
  // seeded lazily, so a rerender from typing in the PIN boxes does not re-read
  // localStorage, and `0` is the honest representation of "not cooling down".
  const [countdown, setCountdown] = useState(() => readMfaResendRemaining());
  // DERIVED, never stored. The old code kept a separate `resendButtonDisabled`
  // flag and the two could disagree — that disagreement is exactly what rendered
  // a greyed-out `Resend(0s)`. With one source there is no such frame: hitting 0
  // enables the link and drops the suffix in the same commit.
  const resendButtonDisabled = countdown > 0;
  const [verifyCode, setVerifyCode] = useState('');
  const [validated, setValidated] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [verifyMfa, { isError, isLoading }] = useLoginMfaVerifyMutation();
  const [resendMfa] = useLoginMfaResendMutation();
  const dispatch = useDispatch();

  const isMobileWidth = document.body.offsetWidth <= 768;
  const redirect = searchParams.get(REDIRECT_SEARCH_PARAM_KEY);

  const submitCode = async () => {
    try {
      verifyMfa({ code: verifyCode, tmpToken })
        .unwrap()
        .then((resp: LoginResponse) => {
          // completed → land; pending → next gate via the shared step-router
          // (routes on `next`; gate order is BE-driven). See login-step.ts.
          advanceLoginStep({ resp, navigate, dispatch, redirect, account });
        })
        .catch(rejected => {
          setErrorMessage(lt('cdb_incorrect_verification_code_text'));
          switch (rejected.status) {
            case 422:
              // wrong code — stay put, show inline error
              break;
            case 410:
              setErrorMessage(lt('cdb_verification_code_expired_text'));
              break;
            case 429:
              setErrorMessage(lt('common_error_try_again_later'));
              break;
            case 401:
            default:
              dispatch(
                addToast({
                  msg: lt('auth_mfa_invalid_credential'),
                  title: (
                    <div className="letter-space3">
                      {lt('cdb_message_status_error_text')}
                    </div>
                  ) as any,
                  type: ToastType.Danger,
                })
              );
              navigate('/login', { state: { account } });
          }
          console.warn(rejected);
        });
    } catch (e) {
      console.warn(e);
    }
  };

  // The dependency is the derived BOOLEAN, not `countdown`: every keystroke in the
  // PIN boxes rerenders this component (onChange -> setValidated), and the old
  // effect had no dependency array at all, so it tore down and rebuilt the 1s
  // interval on each of those renders — typing reset the timer to zero elapsed
  // over and over and the countdown stalled. Keyed on `countdown > 0` the interval
  // is created once when the cooldown starts and disposed once when it ends.
  //
  // Each tick RE-READS the stored instant instead of doing `countdown - 1`, so a
  // throttled background tab or a slept machine resumes at the true value. And
  // reaching 0 STOPS the timer (the flag flips, cleanup runs) rather than
  // restarting at 60, which is what the old else-branch did.
  const isCountingDown = countdown > 0;
  useEffect(() => {
    if (!isCountingDown) {
      return undefined;
    }
    const timerHandler = setInterval(() => {
      setCountdown(readMfaResendRemaining());
    }, 1000);
    return () => clearInterval(timerHandler);
  }, [isCountingDown]);

  const startCounter = () => {
    // The real gate. `.disabled` is colour + cursor only (login-sms.scss) — no
    // `pointer-events: none`, so the handler stays wired and this early return is
    // what makes a cooling-down link inert. Adding pointer-events would also kill
    // the `cursor: not-allowed` the design asks for, so the gate stays here.
    //
    // Both halves are needed. The rendered flag is what the user can see, so a
    // click is never accepted while the label still reads `(Ns)`. The stored
    // instant catches what the flag cannot: two clicks dispatched inside a single
    // React batch both close over the pre-click render and would both get through.
    // A swallowed extra send is expensive — the server allows 5 codes per account
    // per 600s and POST /v1/login already spent one of them.
    if (resendButtonDisabled || readMfaResendRemaining() > 0) {
      return;
    }
    // Armed optimistically, before the reply: a click that comes back 429 is
    // precisely the one that must not be repeated immediately. Matches the
    // pre-existing behaviour of this path.
    armMfaResendCountdown();
    setCountdown(MFA_RESEND_COOLDOWN_SECONDS);

    resendMfa({ tmpToken })
      .unwrap()
      .then((resp: LoginResponse) => {
        // Resend re-sends the code only — BE does NOT re-sign the tmp step token
        // (contract §1.2b 不换票; pending.tmpToken comes back empty). Keep the
        // original Bearer: the truthy guard skips the empty value so `tmpToken`
        // stays put and the next verify carries the still-valid token. Only the
        // masked destination refreshes.
        if (resp.pending?.tmpToken) {
          setTmpToken(resp.pending.tmpToken);
          localStorage.setItem(TEMPORARY, resp.pending.tmpToken);
        }
        if (resp.pending?.mfa?.destMasked) {
          setDestMasked(resp.pending.mfa.destMasked);
          localStorage.setItem(MFA_DEST_MASKED, resp.pending.mfa.destMasked);
        }
        dispatch(
          addToast({
            msg: lt('auth_mfa_send_code_complete_text'),
            title: (
              <div className="letter-space3">
                {lt('cdb_message_status_done_text')}
              </div>
            ) as any,
            type: ToastType.Success,
          })
        );
      })
      .catch(error => {
        if (error?.status === 401) {
          dispatch(
            addToast({
              msg: lt('auth_mfa_send_code_401_error_text'),
              title: (
                <div className="letter-space3">
                  {lt('cdb_message_status_failed_text')}
                </div>
              ) as any,
              type: ToastType.Warning,
            })
          );
          navigate('/login', { state: { account } });
        } else {
          dispatch(
            addToast({
              msg: lt('common_error_try_again_later'),
              title: (
                <div className="letter-space3">
                  {lt('cdb_message_status_failed_text')}
                </div>
              ) as any,
              type: ToastType.Danger,
            })
          );
        }
        console.warn(error);
      });
  };

  const getPhoneNumber = () => destMasked;

  return (
    <LoginBasicBox>
      <div className={styles.container}>
        <header
          className={cx([
            'd-flex flex-row align-items-center justify-content-start',
            styles.header,
          ])}
          style={{ color: 'gray' }}>
          <div
            className={cx(['back', 'd-flex align-items-center'])}
            onClick={() => {
              navigate('/login', { state: { account } });
            }}>
            <span className={cx(['iconfont icon-ico-expand', 'arrowIcon'])} />
            &nbsp;
            <span>{lt('common_back')}</span>
          </div>
        </header>
        <main className={cx([styles.main])}>
          <section className={cx([styles.info])}>
            <section className={cx([styles.title])}>
              <span className="title-bold">{lt('auth_mfa_enter_code')}</span>
            </section>
            <p className={cx(styles.tips)} style={{ color: 'gray' }}>
              <span>{lt('auth_mfa_tips_1')}</span>
            </p>
            <div className={styles.number}>{getPhoneNumber()}</div>
            <section className={cx([styles.pinCodeContainer])}>
              <PinInput
                length={6}
                type="numeric"
                inputMode="number"
                style={{
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                inputStyle={{
                  width: isMobileWidth ? '2.5rem' : '4.375rem',
                  height: isMobileWidth ? '2.5rem' : '4.375rem',
                  margin: isMobileWidth ? '0 0.125rem' : '0 0.92185rem',
                  borderColor: isError ? '#DC0C20' : 'white',
                  borderRadius: '4px',
                  backgroundColor: '#F5F5F5',
                  fontSize: isMobileWidth ? '1rem' : '1.875rem',
                  fontWeight: 600,
                }}
                inputFocusStyle={{
                  borderColor: config?.style?.theme?.primary || '#2f8fa9',
                }}
                onComplete={value => {
                  setVerifyCode(value);
                }}
                onChange={value => {
                  if (VERIFY_CODE_REGEX.test(value)) {
                    setValidated(true);
                  } else {
                    setValidated(false);
                  }
                }}
                autoSelect={true}
                regexCriteria={/^[ 0-9]*$/}
                focus={true}
              />
              {isError && (
                <div className={cx([styles.errorNotice, 'mt-2'])}>
                  <span className={cx(['iconfont icon-ico-warning'])} />
                  &nbsp;<span>{errorMessage}</span>
                </div>
              )}
            </section>
            <section className={cx([styles.resend])}>
              <a
                className={cx([
                  resendButtonDisabled ? 'disabled' : 'activated',
                ])}
                onClick={startCounter}>
                {lt('auth_mfa_send_code')}
                {resendButtonDisabled ? `(${countdown}s)` : ''}
              </a>
            </section>
          </section>
          <section className={cx([styles.actions])}>
            <Button
              className={cx([
                'title-semiBold form-control border-0',
                styles.loginButton,
              ])}
              disabled={!validated || isLoading}
              onClick={submitCode}>
              {isLoading && (
                <span>
                  <span
                    className="spinner-border spinner-border"
                    role="status"
                    aria-hidden="true"
                  />
                  &nbsp;&nbsp;
                </span>
              )}
              <span>{lt('cdb_login_login_text')}</span>
            </Button>
          </section>
        </main>
      </div>
    </LoginBasicBox>
  );
};

export default LoginSms;
